import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import React, { useContext, useEffect, useMemo, useState, useCallback, MouseEvent, JSX } from 'react';
import DOMPurify from 'dompurify';
import {
  Table,
  Button,
  Row,
  Col,
  Card,
  CardHeader,
  CardFooter,
  CardBody,
  CardText,
  Collapse,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'src/components/ui';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router';

import WORKPAGE_COMPONENTS from './fields';
import { checkAccess } from '../../util/useAccessCheck';
import useResourceLoader from '../../util/useResourceLoader';
import fetchApi from '../../util/fetchApi';
import UWEEntityApi from '../../api/UWEEntityApi';
import UweApi from '../../api/UweApi';
import CustomBadge from '../CustomBadge';
import Loader from '../Loader';
import Notification from '../Notification';
import useTitle from '../../util/useTitle';
import Pagination from '../Pagination';
import useSearchParams from '../../util/useSearchParams';
import SearchBar from '../Search/SearchBar';
import SortButton from '../SortButton';
import { AuthContext } from '../../context/AuthContext';
import { getObject, interpolate, mapObject } from '../../util/mapObject';
import WorkTrayIcon from './WorkTrayIcon';
import parseFormDefinition from '../../util/parseFormDefinition';
import ElementIcon from '../ElementIcon';
import { useRouteInterpolation } from '../../util/routeUtil';
import { IDENTITY_FORMATTER, useFormatter } from '../../util/applyFormatting';
import Jnx, { JnxExpr, useJnx } from '../../util/jnx';
import WorkTrayDiagram from './WorkTrayDiagram';
import { useOpenModal } from '../FormModal';
import ModalContainer from '../ModalContainer';
import { EmptyState } from '../Icons/EmptyStates';
import useLoader from '../../util/useLoader';
import RequestExtensionButton from './OcifRequestExtensionButton';
import { useIsMobile } from '../../hooks/useIsMobile';
import classnames from '../../util/classnames';
import { LookupObject, LookupParams } from '../customFields/hooks/useOptionsLookup';
import OcifExtensionsContext from '../../context/OcifExtensionsContext';
import groupBy from 'lodash/groupBy';
import kebabCase from 'lodash/kebabCase';
import { useDarkMode } from '../../context/DarkModeContext';
import useTestId, { getTestId } from '../../hooks/useTestId';
import { useWorkpageTestIdScope } from '../../context/WorkpageTestIdContext';
import Icon from 'src/components/Icon';
import staggerDelay from '../../util/staggerDelay';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ActionRequirement {
  role?: string;
  permission?: string;
  flag?: string;
}

interface ConfirmConfig {
  title?: string;
  text?: string;
  method: string;
  api: string;
}

export interface WorkTrayAction {
  /** Optional stable identifier for testids; falls back to kebab-cased label. */
  name?: string;
  label: string;
  labelExpr?: JnxExpr;
  color?: string;
  backgroundColor?: string;
  route?: string;
  routeExpr?: JnxExpr;
  target?: string;
  onClick?: string | ((rowObject: RowObject, scope: Scope) => void);
  requireExpr?: JnxExpr;
  require?: ActionRequirement[];
  restrict?: ActionRequirement[];
  /** Used to select a specialised button component (e.g. "confirmbutton", "reload"). */
  type?: string;
  confirm?: ConfirmConfig;
  /** Default auto-reload period in seconds (ReloadButton). */
  period?: number;
  periods?: number[];
}

export interface WorkTrayFilter {
  title: string;
  key: string;
  type?: string;
  'ui:field'?: string;
  lookup?: object;
  enum?: string[];
  enumNames?: string[];
  format?: string;
  ignoreIfFalsy?: boolean;
  /** When set, the filter's value (format: "YYYY-MM-DD|YYYY-MM-DD") is expanded to this start-date API param key. */
  startKey?: string;
  /** When set alongside startKey, the filter's value is expanded to this end-date API param key. */
  endKey?: string;
}

export interface WorkTrayColumn {
  title?: string;
  sortkey?: string;
  style?: Record<string, string | number>;
  headerStyle?: Record<string, string | number>;
  'ui:field'?: string;
  actions?: WorkTrayAction[];
  'map:field'?: JnxExpr;
  'map:image'?: JnxExpr;
  showImage?: boolean;
  imageSize?: string;
  iconSize?: string;
  format?: string;
  type?: string;
  checkbox?: boolean;
  color?: string;
  valueTranform?: JnxExpr;
  valueTransform?: JnxExpr;
  isHTML?: boolean;
  emptyText?: string;
  ':classNames'?: string;
}

export interface WorkTrayElement {
  /** Optional stable identifier for testids; falls back to widget type. */
  name?: string;
  title: string;
  description?: string;
  descriptionExpr?: JnxExpr;
  ':className'?: string;
  scope?: Scope;
  'ui:asCard'?: boolean;
  'ui:tableActionsPosition'?: string;
  canCreate?: any;
  canSeeDiagram?: any;
  resource?: string;
  filters?: WorkTrayFilter[];
  tableActions?: WorkTrayAction[];
  columns?: WorkTrayColumn[];
  defaultFilter?: string;
  ignoreTitle?: boolean;
  entityType?: string;
  otherEntityTypes?: string[];
  useMainRoleEntities?: boolean;
  lookup?: LookupObject;
  lookupParams?: LookupParams;
  selectable?: boolean;
  emptyStateIcon?: string;
  emptyStateMessage?: string;
  rowClassExpr?: JnxExpr;
}

type RowObject = Record<string, any>;
type Scope = Record<string, any>;

interface PagedResponse {
  from: number;
  to: number;
  count: number;
  items: RowObject[];
}

interface WorkTrayProps {
  element: WorkTrayElement;
  scope?: Scope;
  selected?: any;
  setSelected?: (value: any) => void;
  formContext?: Object;
  inForm: boolean;
  /** Identifies this element within its Workpage — namespaces its pagination/sort query params (see useSearchParams) so sibling elements don't collide. */
  elementId?: string;
}

/** Tray callbacks handed to special action buttons (e.g. ConfirmButton). */
export interface FnBindings {
  onSearch: (update?: Record<string, string>) => void;
  openModal: ReturnType<typeof useOpenModal>;
  setConfirmError: (error: any) => void;
}

type CompiledAction = WorkTrayAction & { requireJnx?: InstanceType<typeof Jnx> };

function actionSlug(action: WorkTrayAction): string {
  return action.name || kebabCase(action.label) || 'action';
}

function columnSlug(col: WorkTrayColumn, idx: number): string {
  return col.sortkey || (typeof col['map:field'] === 'string' ? col['map:field'] : '') || `col-${idx}`;
}

// ---------------------------------------------------------------------------
// WorkTray (main component)
// ---------------------------------------------------------------------------

// If an expr:-derived lookup param (e.g. metadata-eq-reportId) never resolves — as opposed to
// merely resolving a render late — give up waiting after this long rather than blocking the
// worktray forever, and fetch with that param dropped (the pre-existing fallback behavior).
const PARAMS_PENDING_TIMEOUT_MS = 8000;

function WorkTray(props: WorkTrayProps): JSX.Element {
  const { element, scope: propScope, formContext, inForm = false, elementId } = props;
  const workpageScope = useWorkpageTestIdScope();
  const worktrayBase = useMemo(() => [workpageScope, element.name || 'worktray'].filter(Boolean).join('.'), [workpageScope, element.name]);
  const filterBtnTestId = useTestId(worktrayBase, '.', 'filter-toggle');
  const diagramBtnTestId = useTestId(worktrayBase, '.', 'view-diagram');
  const tableBtnTestId = useTestId(worktrayBase, '.', 'view-table');
  const [lastReload, setLastReload] = useState<number | undefined>();
  const scope = useMemo<Scope>(
    () => ({
      ...propScope,
      ...element?.scope,
      lastReload,
      reload: () => setLastReload(new Date().getTime()),
    }),
    [propScope, lastReload, setLastReload, element]
  );

  const history = useHistory();
  const isMobile = useIsMobile();
  const { isDarkMode } = useDarkMode();

  useTitle(inForm || element.ignoreTitle ? '' : element.title);

  const auth = useContext(AuthContext);
  const { user: { user: { actualOrganizationalUnitId, role: { extras } = {} } = {} } = {} } = auth as any;

  const [formDefinition, sFilters] = parseSearchFormDefinition(element);

  const [filters, setFilters] = useState<Record<string, string>>({ ...sFilters, ...(extras ? JSON.parse(extras).defaultView : {}) });
  const [showFilter, setShowFilter] = useState(false);

  const toggleFilter = (e: MouseEvent) => {
    e.preventDefault();
    setShowFilter(!showFilter);
  };

  const [[offset, setOffset], [size, setSize], [sortBy, setSortBy]] = useSearchParams(
    {
      offset: 0,
      size: 10,
      sortBy: element.defaultFilter,
    },
    elementId
  );

  const [confirmError, setConfirmError] = useState<any>();
  const { lookup, isSelected, setSelected, description } = useWorktrayHooks(props, formContext);
  const rowClassJnx = useJnx(element.rowClassExpr);

  function mergeParamsIntoFilters(
    filters: Record<string, string>,
    params: Record<string, any> | null | undefined,
    element: WorkTrayElement
  ): Record<string, string> {
    if (!params || typeof params !== 'object') {
      return filters;
    }
    const _filters = { ...filters };

    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        _filters[key] = value
          .filter(Boolean)
          .map(String)
          .join(',');
      } else if (value != null && typeof value !== 'object') {
        _filters[key] = String(value);
      }
    });

    const arrayKeys = groupBy(
      element?.filters?.filter(f => f.arrayKey),
      f => f.arrayKey
    );

    Object.keys(arrayKeys).forEach(arrKey => {
      const list = arrayKeys[arrKey];
      const resultArray = [];
      list.forEach(f => {
        if (_filters[f.key]) resultArray.push(_filters[f.key]);
        delete _filters[f.key];
      });
      _filters[arrKey] = resultArray;
    });

    return _filters;
  }

  const [paramsPendingTimedOut, setParamsPendingTimedOut] = useState(false);

  useEffect(() => {
    if (!lookup.paramsPending) {
      setParamsPendingTimedOut(false);
      return undefined;
    }

    const timer = setTimeout(() => setParamsPendingTimedOut(true), PARAMS_PENDING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [lookup.paramsPending]);

  const [paging, resourceLoading = true, error] = useResourceLoader<PagedResponse | null>(
    async abortController => {
      const entity = Array.isArray(lookup.entity) ? lookup.entity.join(',') : lookup.entity;

      let orgUnitId = actualOrganizationalUnitId;

      if (!orgUnitId && (auth as any)?.user?.organizationalUnitRoles?.length > 0) {
        orgUnitId = (auth as any).user.organizationalUnitRoles[0].organizationalUnitId;
      }

      const resource = lookup.entity ? `uwe-entities/${encodeURIComponent(entity!)}/list/${orgUnitId}` : lookup.api?.split(':')[1];
      if (!resource) return null;
      // expr:-derived lookup params (e.g. metadata-eq-reportId) can resolve one render late via formContext —
      // don't fire the list request until they're in, or it returns an unfiltered/wrong result set.
      // If they never resolve, paramsPendingTimedOut lets us give up waiting instead of blocking forever.
      if (lookup.paramsPending && !paramsPendingTimedOut) return useResourceLoader.DoNotSet;
      const _filters = mergeParamsIntoFilters(filters, lookup?.params, element);
      const apiParams: Record<string, any> = { ..._filters };
      element.filters?.forEach(f => {
        if (f.startKey && apiParams[f.key]) {
          const [startDate, endDate] = String(apiParams[f.key]).split('|');
          delete apiParams[f.key];
          if (startDate) apiParams[f.startKey] = startDate;
          if (endDate && f.endKey) apiParams[f.endKey] = endDate;
        }
        if (f.ignoreIfFalsy) {
          const v = apiParams[f.key];
          if (!v || v === 'false' || v === '0') delete apiParams[f.key];
        }
      });
      const data = await UWEEntityApi.getWorkTray({ resource, offset, size, sortBy, ...apiParams, abortController });

      return Array.isArray(data)
        ? {
            from: 0,
            to: data.length,
            count: data.length,
            items: data,
          }
        : data;
    },
    [size, offset, sortBy, filters, element, lookup, lastReload, actualOrganizationalUnitId, auth, paramsPendingTimedOut]
  );

  // Keep showing the loader (instead of a false "empty" flash) while expr:-derived lookup params
  // haven't resolved yet — the fetch above bails out early via DoNotSet in that case. Once
  // paramsPendingTimedOut flips, stop treating it as loading even if the param is still unresolved.
  const loading = resourceLoading || (lookup.paramsPending && !paramsPendingTimedOut);

  const { from = 0, to = 0, count = 0, items: data = [] } = paging || {};

  const onSearch = (update?: Record<string, string>) => {
    setFilters({ ...filters, ...update });
    setOffset(undefined);
    if (isMobile) setShowFilter(false);
  };

  const onClearSearch = () => {
    setFilters({});
    setOffset(undefined);
  };

  const [tab, setTab] = useState('table');

  const openModal = useOpenModal();

  const fnBindings: FnBindings = {
    onSearch,
    openModal,
    setConfirmError,
  };

  // Only use flat layout when explicitly set to false — null/undefined keeps card layout
  const asCard = element['ui:asCard'] !== false;

  const descriptionContent = () => {
    let desc = element.description;
    if (description) {
      desc = description;
    }

    if (desc) {
      return (
        <div className="worktray-description" style={{ position: 'relative', bottom: '15px' }}>
          <small>{desc}</small>
        </div>
      );
    }

    return null;
  };

  const filtersApplied = !!(element.filters?.length && Object.values(filters).some(Boolean));

  // ── Shared table body (used in both layouts) ──────────────────────────────
  const tableContent = (
    <>
      <div className={classnames('worktray-table-container', { 'full-height': loading || (data?.length || 0) === 0 })}>
        {loading && (
          <div
            className="loader-container h-full w-full flex justify-center items-center"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 5,
              backgroundColor: isDarkMode ? 'rgba(34,36,42,0.9)' : 'rgba(255,255,255,0.9)',
            }}
          >
            <Loader>
              <strong>
                <Trans>Loading {element.title}</Trans>...
              </strong>
            </Loader>
          </div>
        )}
        <Table className="hide-when-mobile animated-show" striped borderless hover>
          <thead>
            <tr>
              {element?.columns?.map(({ sortkey, title, style = {}, headerStyle = {}, actions }, idx) => {
                const align: Record<string, string> = { textAlign: (actions ?? []).length > 1 ? 'center' : 'left' };
                return sortkey ? (
                  <SortButton
                    key={idx}
                    tag="th"
                    sortKey={sortkey}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    style={{ ...style, ...headerStyle, ...align }}
                  >
                    {title}
                  </SortButton>
                ) : (
                  <th key={idx} style={{ ...style, ...headerStyle, ...align }}>
                    {title}
                  </th>
                );
              })}
            </tr>
            {error || confirmError ? (
              <tr className="error-container">
                <td colSpan={8}>
                  <Notification
                    error={error || confirmError}
                    onClose={() => {
                      setConfirmError(undefined);
                    }}
                  />
                </td>
              </tr>
            ) : null}
          </thead>
          <tbody>
            {loading ? (
              <></>
            ) : (
              <>
                {data && data.length ? (
                  data.map((rowObject, idx) => (
                    <DataRow
                      key={idx}
                      columns={element.columns}
                      rowObject={rowObject}
                      scope={scope}
                      selected={isSelected(rowObject)}
                      setSelected={setSelected}
                      fnBindings={fnBindings}
                      testIdBase={worktrayBase}
                      rowIndex={idx}
                      rowClassJnx={rowClassJnx}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan={100}>
                      <EmptyState
                        icon={element.emptyStateIcon}
                        message={element.emptyStateMessage ?? t`The list of ${element.title} is empty.`}
                        size={150}
                        filtersApplied={filtersApplied}
                        clearFilters={filtersApplied ? onClearSearch : null}
                      />
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </Table>
      </div>
      <div className="mobile-table show-when-mobile">
        {loading ? (
          <div className="loader-container">
            <Loader>
              <Trans>Loading {element.title}</Trans>
            </Loader>
          </div>
        ) : (
          <>
            {error || confirmError ? (
              <div className="error-container">
                <Notification
                  error={error || confirmError}
                  onClose={() => {
                    setConfirmError(undefined);
                  }}
                />
              </div>
            ) : null}
            {data && data.length ? (
              data.map((rowObject, idx) => (
                <MobileDataRow
                  key={idx}
                  columns={element.columns}
                  rowObject={rowObject}
                  scope={scope}
                  selected={isSelected(rowObject)}
                  setSelected={setSelected}
                  fnBindings={fnBindings}
                  testIdBase={`${worktrayBase}.mobile`}
                  rowIndex={idx}
                />
              ))
            ) : (
              <EmptyState
                icon={element.emptyStateIcon}
                message={element.emptyStateMessage ?? t`The list of ${element.title} is empty.`}
                size={120}
                filtersApplied={filtersApplied}
                clearFilters={filtersApplied ? onClearSearch : null}
              />
            )}
          </>
        )}
      </div>
    </>
  );

  let tableActionsOnRight = false;
  if (element['ui:tableActionsPosition'] === 'right') {
    tableActionsOnRight = true;
  }

  const tableActions =
    element.tableActions && element.tableActions.length > 0 ? (
      <ActionButtonsCell actions={element.tableActions} scope={scope} fnBindings={fnBindings} testIdBase={worktrayBase} />
    ) : null;

  const filterBtn =
    element.filters && element.filters.length > 0 ? (
      <Button onClick={toggleFilter} type="button" className="filter-button" outline color="white" active={showFilter} {...filterBtnTestId}>
        <Icon icon="fa fa-filter" />
      </Button>
    ) : null;

  // ── Flat layout (ui:asCard === false) ─────────────────────────────────────
  if (!asCard) {
    return (
      <div className={classnames('worktray', 'worktray--flat', element[':className'])}>
        <ModalContainer />
        {element.canCreate ? <WorkTrayIcon element={element.canCreate} scope={scope} /> : null}

        {/* Header: title left, filter button right */}
        <div className="worktray-flat-header">
          <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 0, gap: 16 }}>
            <div className={`title ${element.tableActions ? 'has-buttons' : ''}`}>
              <h5 className="mb-0">{element.title}</h5>
            </div>
            {tableActionsOnRight && filterBtn != null && (
              <Col sm="1" xs={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <small>{filterBtn}</small>
              </Col>
            )}
            {!tableActionsOnRight && <div className="flex items-center gap-1">{tableActions}</div>}

            {element.canSeeDiagram ? (
              <div className="float-right">
                {tab !== 'diagram' ? (
                  <Button size="sm" className="gap-1" color="secondary" onClick={() => setTab('diagram')} {...diagramBtnTestId}>
                    <Icon icon="Network" /> <Trans>Diagram View</Trans>
                  </Button>
                ) : (
                  <Button size="sm" className="gap-1" color="secondary" onClick={() => setTab('table')} {...tableBtnTestId}>
                    <Icon icon="Table" /> <Trans>Table View</Trans>
                  </Button>
                )}
              </div>
            ) : null}
          </div>
          {tableActionsOnRight && <div className="flex items-center gap-1">{tableActions}</div>}
          {!tableActionsOnRight && filterBtn != null && (
            <Col sm="1" xs={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <small>{filterBtn}</small>
            </Col>
          )}
        </div>

        {descriptionContent()}

        {/* Collapsible filter bar */}
        {formDefinition ? (
          <Collapse isOpen={showFilter} className="worktray-filters-collapse">
            <SearchBar formDefinition={formDefinition} filters={filters} onSearch={onSearch} onClearSearch={onClearSearch} />
          </Collapse>
        ) : null}

        {/* Scrollable table area */}
        <div className="worktray-flat-scroll">
          {element.canSeeDiagram && tab === 'diagram' ? (
            <WorkTrayDiagram resource={element.resource} dataDiagram={element.canSeeDiagram} history={history} />
          ) : (
            tableContent
          )}
        </div>

        {/* Pagination always at bottom */}
        {paging && paging?.count > paging?.items?.length && (
          <div className="worktray-flat-pagination">
            <Pagination offset={from} count={count} size={size} setSize={setSize} setOffset={setOffset} />
          </div>
        )}
      </div>
    );
  }

  // ── Default card layout ───────────────────────────────────────────────────
  return (
    <>
      <div
        className={classnames('worktray', element[':className'])}
        onClick={e => {
          e.preventDefault();
        }}
        role="table"
      >
        <ModalContainer />
        {element.canCreate ? <WorkTrayIcon element={element.canCreate} scope={scope} /> : null}
        <Card className={'animated-show inbox-card primary no-bottom-gap'}>
          <CardHeader>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="worktray-header-left">
                <div className={`title ${element.tableActions ? 'has-buttons' : ''}`}>
                  <span>{element.title}</span>
                  {descriptionContent()}
                </div>
                {tableActionsOnRight && filterBtn}
                {!tableActionsOnRight && tableActions}
              </div>
              {element.canSeeDiagram ? (
                <div className="float-right">
                  {tab !== 'diagram' ? (
                    <Button size="sm" color="secondary" onClick={() => setTab('diagram')} {...diagramBtnTestId}>
                      <Icon icon="Network" /> <Trans>Diagram View</Trans>
                    </Button>
                  ) : (
                    <Button size="sm" color="secondary" onClick={() => setTab('table')} {...tableBtnTestId}>
                      <Icon icon="Table" /> <Trans>Table View</Trans>
                    </Button>
                  )}
                </div>
              ) : null}
              {tableActionsOnRight && tableActions}
              {!tableActionsOnRight && filterBtn}
            </div>
          </CardHeader>
          {formDefinition ? (
            <Collapse isOpen={showFilter} className="worktray-filters-collapse">
              <SearchBar formDefinition={formDefinition} filters={filters} onSearch={onSearch} onClearSearch={onClearSearch} />
            </Collapse>
          ) : null}
        </Card>
        <Card className={`animated-show inbox-card ${showFilter ? '' : 'square-top'}`}>
          {element.canSeeDiagram && tab === 'diagram' ? (
            <WorkTrayDiagram resource={element.resource} dataDiagram={element.canSeeDiagram} history={history} />
          ) : (
            <>
              {tableContent}
              {paging && paging?.count > paging?.items?.length && (
                <CardFooter className="pagination">
                  <Pagination offset={from} count={count} size={size} setSize={setSize} setOffset={setOffset} />
                </CardFooter>
              )}
            </>
          )}
        </Card>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// useWorktrayHooks
// ---------------------------------------------------------------------------

interface WorktrayHooksResult {
  lookup: LookupObject & { paramsJnx: Record<string, InstanceType<typeof Jnx>>; params: Record<string, any>; paramsPending: boolean };
  isSelected: (rowObject: RowObject) => boolean;
  setSelected: ((rowObject: RowObject) => void) | undefined;
  description: string;
}

function useWorktrayHooks(
  { element, selected: propSelected, setSelected: propSetSelected }: WorkTrayProps,
  formContext?: Object
): WorktrayHooksResult {
  const { selectable } = element;
  const auth = useContext(AuthContext);

  const _lookup = useMemo(() => {
    let lookup: LookupObject;

    if (element.lookup) {
      lookup = { ...element.lookup };
    } else {
      let entityType: string | string[] | undefined;

      if (element.useMainRoleEntities) {
        const userRoles: any[] = (auth as any)?.user?.user?.roles || [];
        const mainRole = userRoles.find((x: any) => x.mainRole) ?? userRoles[0];
        entityType = Object.entries(mainRole?.role?.entities ?? {})
          .map(([key, { homeTray }]: [string, any]) => (homeTray ? key : null))
          .filter((x): x is string => !!x);
      } else {
        entityType = element.entityType;

        if (element.otherEntityTypes?.length) {
          entityType = [entityType, ...element.otherEntityTypes].filter((x): x is string => !!x);
        }
      }

      lookup = {
        entity: entityType,
        api: !element.entityType && element.resource ? `api:${element.resource}` : null,
        params: element.lookupParams,
      };
    }

    const paramsJnx: Record<string, InstanceType<typeof Jnx>> = {};
    const params: Record<string, any> = {};

    Object.entries(lookup.params ?? {}).forEach(([k, v]) => {
      if (k.startsWith('expr:')) {
        paramsJnx[k.replace(/^expr:/, '')] = new Jnx(v);
      } else {
        params[k] = v;
      }
    });

    return { ...lookup, params, paramsJnx };
  }, [element?.lookup, element, auth]);

  const idAttr = (_lookup as any).id;

  const bindings = useMemo(
    () => ({
      auth,
      idAttr,
      formContext,
    }),
    [auth, idAttr, formContext]
  );

  const lookup = useMemo(() => {
    let paramsPending = false;

    const evaluatedParams = Object.entries(_lookup.paramsJnx).reduce<Record<string, any>>((_, [k, jnx]) => {
      const value = jnx.eval({}, '', bindings);
      if (value === undefined || value === null) paramsPending = true;
      _[k] = value;

      return _;
    }, {});

    return {
      ..._lookup,
      paramsPending,
      params: {
        ..._lookup.params,
        ...evaluatedParams,
      },
    };
  }, [_lookup, bindings]);

  const { isSelected, setSelected } = useMemo(() => {
    let isSelected: (x: RowObject) => boolean = () => false;
    let setSelected: ((x: RowObject) => void) | undefined;

    if (selectable) {
      const valueFn = idAttr ? (x: RowObject) => x[idAttr] : (x: RowObject) => x;

      isSelected = (x: RowObject) => {
        const xValue = valueFn(x);

        return xValue === propSelected;
      };

      setSelected = (x: RowObject) => propSetSelected!(valueFn(x));
    }

    return { isSelected, setSelected };
  }, [selectable, idAttr, propSelected]);

  const descriptionJnx = useJnx(element.descriptionExpr);

  const description = useMemo(() => {
    if (element.descriptionExpr && descriptionJnx) {
      return descriptionJnx.eval({}, '', { ...bindings });
    }
    return '';
  }, [element, descriptionJnx, bindings]);

  return {
    lookup,
    isSelected,
    setSelected,
    description,
  };
}

WorkTray.rootSchema = {
  definitions: {
    mapFieldField: {
      type: ['string', 'object'],
      'ui:field': 'JsonField',
    },
    requireRestrictfield: {
      type: 'array',
      ':classNames': 'from-col-1-size-2',
      'ui:arrayType': 'cards',
      items: {
        type: 'object',
        'ui:addButtonText': 'Add Option',
        'ui:positionButtons': 'top-outside',
        properties: {
          flag: {
            title: t`User Flag`,
            type: 'string',
          },
          role: {
            title: t`Role`,
            type: 'string',
            'ui:field': 'LookupFormField',
            lookup: {
              api: 'roles',
              resource: t`Roles`,
              id: 'name',
              label: 'name',
            },
          },
          permission: {
            title: t`Permission`,
            type: 'string',
            enum: ['canWorkAs', 'canBeAssigned', 'canAssign'],
            enumNames: [t`Can Work`, t`Can be Assigned`, t`Can Assign`],
          },
        },
        'ui:field': 'ToggleObjectField',
      },
    },
    actionsField: {
      type: 'array',
      title: 'Actions',
      'ui:arrayType': 'cards',
      'ui:newRow': true,
      items: {
        type: 'object',
        'ui:addButtonText': 'Add Action',
        'ui:positionButtons': 'top',
        'ui:sectionType': 'subsubsection',
        properties: {
          obj: {
            type: 'object',
            ':classNames': 'grid-2-columns',
            title: ['# ${$index + 1}: ${label}'].join(''),
            'ui:newSection': true,
            'ui:expandable': true,
            properties: {
              label: {
                title: 'Action',
                type: 'string',
                'map:field': 'label',
              },
              backgroundColor: {
                title: 'Color',
                type: 'string',
                'map:field': 'backgroundColor',
              },
              route: {
                title: 'Route',
                type: 'string',
                'map:field': 'route',
              },
              icon: {
                title: 'Icon',
                type: 'string',
                'map:field': 'icon',
              },
              routeExpr: {
                title: 'routeExpr',
                ':classNames': 'from-col-1-size-1',
                type: ['string', 'object'],
                'ui:field': 'JsonField',
                'map:field': 'routeExpr',
              },
              requireExpr: {
                title: 'requireExpr',
                type: ['string', 'object'],
                'ui:field': 'JsonField',
                'map:field': 'requireExpr',
              },
              requirerestrict: {
                type: 'object',
                ':classNames': 'from-col-1-size-2',
                title: 'Conditions',
                'ui:sectionType': 'label',
                'ui:field': 'ToggleObjectField',
                properties: {
                  require: {
                    title: 'Require Any',
                    'map:field': 'require',
                    $$include: '#/definitions/requireRestrictfield',
                  },
                  restrict: {
                    title: 'Restrict If Any',
                    'map:field': 'restrict',
                    $$include: '#/definitions/requireRestrictfield',
                  },
                },
              },
            },
          },
        },
      },
    },
  },
  properties: {
    asCard: {
      title: 'Display as card',
      type: 'boolean',
      'map:field': 'ui:asCard',
    },
    tableActionsPosition: {
      title: 'Table actions position',
      type: 'string',
      enum: ['left', 'right'],
      'map:field': 'ui:tableActionsPosition',
    },
    title: {
      title: 'Title',
      type: 'string',
      'map:field': 'title',
    },
    description: {
      title: 'Description',
      type: 'string',
      'map:field': 'description',
    },
    className: {
      title: 'Class Names',
      type: 'string',
      'map:field': ':classNames',
    },
    eT: {
      type: 'object',
      title: ' ',
      properties: {
        entityType: {
          title: 'Entity Type',
          type: 'string',
          'map:field': 'entityType',
          'ui:field': 'LookupFormField',
          lookup: {
            resource: 'Entity Types',
            api: 'api:manage/objectschemas',
            params: { all: true },
            options: { useCache: true },
            jnx: 'data.items',
            id: 'name',
            label: 'name',
          },
        },
        otherEntityTypes: {
          title: ' ',
          type: 'array',
          'map:field': 'otherEntityTypes',
          'ui:sectionType': 'label',
          items: {
            'ui:field': 'LookupFormField',
            title: ' ',
            required: false,
            min: 0,
            'ui:orderable': false,
            type: 'string',
            lookup: {
              resource: 'Entity Types',
              api: 'api:manage/objectschemas',
              params: { all: true },
              options: { useCache: true },
              jnx: 'data.items',
              id: 'name',
              label: 'name',
            },
          },
        },
        useMainRoleEntities: {
          title: 'Show all entity types from main role',
          type: 'boolean',
          'map:field': 'useMainRoleEntities',
        },
      },
    },
    resource: {
      title: 'Resource',
      type: 'string',
      'map:field': 'resource',
    },
    lookupParams: {
      title: 'Params',
      type: 'object',
      'map:field': 'lookupParams',
      properties: {},
      additionalProperties: {
        type: 'string',
        title: 'value',
      },
      'ui:asPropertiesTable': true,
    },
    tableActions: {
      $$include: '#/definitions/actionsField',
      ':classNames': 'from-col-1-size-3',
      'map:array': 'tableActions',
    },
    defaultFilter: {
      title: 'Default Filter',
      type: 'string',
      'map:field': 'defaultFilter',
    },
    filters: {
      title: 'Filters',
      ':classNames': 'from-col-1-size-3',
      type: 'array',
      'map:array': 'filters',
      items: {
        type: 'object',
        properties: {
          title: {
            type: 'string',
            title: 'title',
            'map:field': 'title',
          },
          key: {
            type: 'string',
            title: 'key',
            'map:field': 'key',
          },
          'ui:field': {
            type: 'string',
            title: 'Tipo de Campo',
            'map:field': 'ui:field',
          },
          lookup: {
            type: 'string',
            'ui:field': 'JsonField',
            title: 'lookup',
            'map:field': 'lookup',
          },
          type: {
            type: 'string',
            title: 'type',
            'map:field': 'type',
          },
          format: {
            type: 'string',
            title: 'format',
            'map:field': 'format',
          },
          ignoreIfFalsy: {
            type: 'boolean',
            title: 'Ignore if falsy',
            'map:field': 'ignoreIfFalsy',
          },
        },
      },
    },
    columns: {
      title: 'Columns',
      ':classNames': 'from-col-1-size-3',
      type: 'array',
      'map:array': 'columns',
      items: {
        type: 'object',
        'ui:showColumnsIf': {
          actions: { expr: 'hasActions', debug: 'actions showColumnsIf', scope: '..' },
        },
        properties: {
          title: {
            type: 'string',
            'map:field': 'title',
            title: 'Title',
          },
          sortkey: {
            type: 'string',
            'map:field': 'sortkey',
            title: 'Key',
          },
          mapField: {
            $$include: '#/definitions/mapFieldField',
            'map:field': 'map:field',
            title: 'Field',
          },
          format: {
            type: 'string',
            'map:field': 'format',
            title: 'format',
          },
          panel2: {
            'ui:newRow': true,
            type: 'object',
            title: 'Extra Properties...',
            'ui:field': 'HideObjectField',
            'ui:newSection': true,
            'ui:sectionType': 'subsubsection',
            'ui:fromBottom': true,
            'ui:expandable': true,
            'ui:expandedDefault': false,
            ':classNames': 'grid-2-columns bordered',
            properties: {
              color: {
                $$include: '#/definitions/mapFieldField',
                'map:field': 'color',
                title: 'Color',
              },
              valueTranform: {
                $$include: '#/definitions/mapFieldField',
                'map:field': 'valueTranform',
                title: 'Value Tranform Expression',
              },
              mapImage: {
                $$include: '#/definitions/mapFieldField',
                ':classNames': 'from-col-1-size-1',
                'map:field': 'map:image',
                title: 'Image',
              },
              iconSize: {
                title: 'Icon Size',
                'ui:showIf': { scope: '.', expr: '$isTruthy(mapImage)', debug: 'iconSize showIf' },
                type: 'string',
                enum: ['1em', '2em', '4em', '6em', '8em', '10em'],
                'map:field': { default: 'icons.size', path: 'fontSize' },
              },
              type: {
                $$include: '#/definitions/mapFieldField',
                'map:field': 'type',
                title: 'Type',
              },
              actions: {
                $$include: '#/definitions/actionsField',
                ':classNames': 'from-col-1-size-2',
                'map:array': 'actions',
              },
            },
          },
        },
      },
    },
  },
};

// ---------------------------------------------------------------------------
// DataRow
// ---------------------------------------------------------------------------

interface DataRowProps {
  rowObject: RowObject;
  columns?: WorkTrayColumn[];
  scope: Scope;
  selected: boolean;
  setSelected: ((rowObject: RowObject) => void) | undefined;
  fnBindings: FnBindings;
  testIdBase?: string;
  rowIndex?: number;
  rowClassJnx?: InstanceType<typeof Jnx> | null;
}

function DataRow({ rowObject, columns, scope, selected, setSelected, fnBindings, testIdBase, rowIndex, rowClassJnx }: DataRowProps): JSX.Element {
  const rowKey = rowObject?.id ?? rowIndex;
  const rowTestIdProps = getTestId(testIdBase, '.', 'row', '-', rowKey);
  const rowClass = useMemo(() => rowClassJnx?.eval(rowObject, '', scope) ?? '', [rowClassJnx, rowObject, scope]);

  const row = useMemo(() => {
    return columns?.map((cell, idx) => {
      const cellSlug = columnSlug(cell, idx);
      const cellBase = testIdBase ? `${testIdBase}.row-${rowKey}.${cellSlug}` : undefined;
      if (cell['ui:field'] && WORKPAGE_COMPONENTS[cell['ui:field']] != null) {
        const Component = WORKPAGE_COMPONENTS[cell['ui:field']];

        return (
          <td key={idx} style={cell.style}>
            <Component rowObject={rowObject} scope={scope} />
          </td>
        );
      }

      return (
        <td key={idx} style={cell.style} className={classnames({ 'action-buttons-cell': cell.actions?.length }, rowClass)}>
          {cell.actions?.length ? (
            <ActionButtonsCell fnBindings={fnBindings} actions={cell.actions} rowObject={rowObject} scope={scope} testIdBase={cellBase} />
          ) : (
            <DataCell cell={cell} rowObject={rowObject} selected={selected} setSelected={setSelected} scope={scope} testIdBase={cellBase} />
          )}
        </td>
      );
    });
  }, [columns, rowObject, selected, setSelected, scope, fnBindings, testIdBase, rowKey, rowClass]);

  return (
    <tr className={classnames('stagger-item', rowClass)} style={{ animationDelay: `${staggerDelay(rowIndex ?? 0)}ms` }} {...rowTestIdProps}>
      {row}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// MobileDataRow
// ---------------------------------------------------------------------------

interface MobileDataRowProps {
  rowObject: RowObject;
  columns?: WorkTrayColumn[];
  scope: Scope;
  selected: boolean;
  setSelected: ((rowObject: RowObject) => void) | undefined;
  fnBindings: FnBindings;
  testIdBase?: string;
  rowIndex?: number;
}

function MobileDataRow({ rowObject, columns, scope, selected, setSelected, fnBindings, testIdBase, rowIndex }: MobileDataRowProps): JSX.Element {
  const rowKey = rowObject?.id ?? rowIndex;
  const rowTestIdProps = getTestId(testIdBase, '.', 'row', '-', rowKey);
  const row = useMemo(() => {
    return columns?.map((cell, idx) => {
      const cellSlug = columnSlug(cell, idx);
      const cellBase = testIdBase ? `${testIdBase}.row-${rowKey}.${cellSlug}` : undefined;
      if (cell['ui:field'] && WORKPAGE_COMPONENTS[cell['ui:field']] != null) {
        const Component = WORKPAGE_COMPONENTS[cell['ui:field']];

        return (
          <React.Fragment key={idx}>
            {cell.title ? <span>{cell.title}:</span> : null}
            <Component rowObject={rowObject} scope={scope} />
          </React.Fragment>
        );
      }

      return cell.actions && cell.actions.length ? (
        <ActionButtonsCell key={idx} fnBindings={fnBindings} actions={cell.actions} rowObject={rowObject} scope={scope} testIdBase={cellBase} />
      ) : (
        <CardText key={idx}>
          <MobileDataCell cell={cell} rowObject={rowObject} selected={selected} setSelected={setSelected} testIdBase={cellBase} />
        </CardText>
      );
    });
  }, [columns, rowObject, selected, setSelected, scope, fnBindings, testIdBase, rowKey]);

  return (
    <Card className="stagger-item" style={{ animationDelay: `${staggerDelay(rowIndex ?? 0)}ms` }} {...rowTestIdProps}>
      <CardBody>{row}</CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// MobileDataCell
// ---------------------------------------------------------------------------

interface MobileDataCellProps {
  rowObject: RowObject;
  cell: WorkTrayColumn;
  selected: boolean;
  setSelected: ((rowObject: RowObject) => void) | undefined;
  testIdBase?: string;
}

function MobileDataCell({ rowObject, cell, selected, setSelected, testIdBase }: MobileDataCellProps): JSX.Element {
  const { 'map:field': mapField, 'map:image': mapImage, showImage, iconSize, format, type, checkbox, color: colorExpr, title } = cell;
  const emptyText = cell.emptyText || (checkbox ? '' : '---');

  const { i18n } = useLingui();
  const colorJnx = useJnx(colorExpr);

  const value = useMemo(() => (mapField ? mapObject(rowObject, { value: mapField }).value : null), [rowObject, mapField]);
  const color = useMemo(() => colorJnx?.eval(rowObject, '', { value }), [value, colorJnx, rowObject]);
  const formatter = useFormatter(format);
  const text = useMemo(
    () => (formatter !== IDENTITY_FORMATTER ? formatter.apply(value, i18n.locale) : value !== null && value !== undefined ? `${value}` : ''),
    [value, formatter, i18n.locale]
  );

  const { image: attachedImage } = rowObject;
  const image = useMemo(() => {
    if (showImage) {
      if (attachedImage?.url) return { image: attachedImage.url };
      return { icon: 'Link' };
    }
    return mapImage ? mapObject(rowObject, { value: mapImage }).value : null;
  }, [showImage, attachedImage, rowObject, mapImage]);

  const onClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      setSelected!(rowObject);
    },
    [rowObject, setSelected]
  );

  const flag = mapField ? value : selected;

  return (
    <>
      {image ? <ElementIcon element={image} fontSize={iconSize} /> : null}
      {image ? ' ' : null}
      {checkbox ? (
        <button
          className={classnames('icon-check-field', 'btn btn-sm', { 'btn-success': flag, 'btn-secondary': !flag })}
          color="secondary"
          onClick={onClick}
          {...getTestId(testIdBase, '.', 'checkbox')}
        >
          {flag ? <Icon icon="Check" /> : null}
        </button>
      ) : null}
      {type === 'badge' ? (
        <CustomBadge color={color} style={{ fontSize: '100%' }} pill>
          {text || emptyText}
        </CustomBadge>
      ) : (
        `${title ? `${title}: ` : ''}${text || emptyText}`
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// DataCell
// ---------------------------------------------------------------------------

interface DataCellProps {
  rowObject: RowObject;
  cell: WorkTrayColumn;
  selected: boolean;
  setSelected: ((rowObject: RowObject) => void) | undefined;
  scope: Scope;
  testIdBase?: string;
}

function DataCell({ rowObject, cell, selected, setSelected, scope, testIdBase }: DataCellProps): JSX.Element {
  const {
    'map:field': mapField,
    ':classNames': classNames,
    'map:image': mapImage,
    showImage,
    imageSize,
    iconSize,
    format,
    type,
    checkbox,
    color: colorExpr,
    valueTranform: valueTranformExpr,
    valueTransform: valueTransformExpr,
    isHTML = false,
  } = cell;
  const emptyText = cell.emptyText || (checkbox ? '' : '---');

  const { i18n } = useLingui();
  const { image: attachedImage } = rowObject;

  const colorJnx = useJnx(colorExpr);
  const valueTranformJnx = useJnx(valueTranformExpr ? valueTranformExpr : valueTransformExpr);

  const value = useMemo(() => {
    let value = mapField ? mapObject(rowObject, { value: mapField }, undefined, scope).value : null;

    if (valueTranformJnx) value = valueTranformJnx.eval(rowObject, '', { value, scope });

    return value;
  }, [rowObject, mapField, valueTranformJnx, scope]);

  const color = useMemo(() => colorJnx?.eval(rowObject, '', { value }), [value, colorJnx, rowObject]);
  const formatter = useFormatter(format);
  const text = useMemo(
    () => (formatter !== IDENTITY_FORMATTER ? formatter.apply(value, i18n.locale) : value !== null && value !== undefined ? `${value}` : ''),
    [value, formatter, i18n.locale]
  );

  const image = useMemo(() => {
    if (showImage) {
      if (attachedImage?.url) return { image: attachedImage.url };
      return { icon: 'Link' };
    }
    return mapImage ? mapObject(rowObject, { value: mapImage }).value : null;
  }, [showImage, attachedImage, rowObject, mapImage]);

  const onClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      setSelected!(rowObject);
    },
    [rowObject, setSelected]
  );

  const flag = mapField ? value : selected;

  return (
    <div className={classnames('worktray-datacell', classNames)} {...getTestId(testIdBase)}>
      {image ? <ElementIcon element={image} fontSize={iconSize} size={imageSize} /> : null}
      {image ? ' ' : null}
      {checkbox ? (
        <button
          className={classnames('icon-check-field', 'btn btn-sm', { 'btn-success': flag, 'btn-secondary': !flag })}
          color="secondary"
          onClick={onClick}
          {...getTestId(testIdBase, '.', 'checkbox')}
        >
          {flag ? <Icon icon="Check" /> : null}
        </button>
      ) : null}
      {type === 'badge' ? (
        <CustomBadge color={color} style={{ fontSize: '100%' }} pill>
          {text || emptyText}
        </CustomBadge>
      ) : isHTML ? (
        HTMLCodeDisplay(text)
      ) : (
        text || emptyText || ''
      )}
    </div>
  );
}

const HTMLCodeDisplay = (htmlCode: string): JSX.Element => {
  const sanitizedHTML = useMemo(
    () => ({
      __html: DOMPurify.sanitize(htmlCode),
    }),
    [htmlCode]
  );

  return <div dangerouslySetInnerHTML={sanitizedHTML} />;
};

// ---------------------------------------------------------------------------
// OcifBulkHolidayButtons
// ---------------------------------------------------------------------------

interface OcifBulkHolidayButtonsProps {
  action: CompiledAction & {
    template?: string;
    filename?: string;
    templateLabel?: string;
    api?: string;
  };
  scope: Scope;
  fn: FnBindings;
  testIdBase?: string;
}

function OcifBulkHolidayButtons({ action, fn, testIdBase }: OcifBulkHolidayButtonsProps): JSX.Element | null {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string>('');

  if (!action.api) return null;

  function downloadTemplate() {
    const BOM = '\uFEFF';
    const blob = new Blob([BOM, action.template!], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = action.filename ?? 'template.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  function triggerPicker() {
    setStatus('idle');
    setMessage('');
    inputRef.current?.click();
  }

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setStatus('uploading');
    try {
      const json = await fetchApi.api.post(action.api!, { formdata: { file } });
      const count: number = json?.data ?? json;
      setMessage(`${count} holiday(s) created.`);
      setStatus('success');
      fn.onSearch();
    } catch (err) {
      setMessage(err?.message ?? 'Upload failed');
      setStatus('error');
    }
  }

  const uploadColor = status === 'error' ? 'danger' : status === 'success' ? 'success' : action.color || 'primary';

  return (
    <div style={{ display: 'flex', gap: '5px' }}>
      <input ref={inputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onFileChange} />
      <Button
        size="sm"
        color={uploadColor}
        disabled={status === 'uploading'}
        {...getTestId(testIdBase, '.', 'upload')}
        onClick={triggerPicker}
        title={message || undefined}
      >
        <Icon icon="Upload" className="mr-1" />
        {status === 'uploading' ? <Trans>Uploading…</Trans> : action.label}
      </Button>
      {action.template && (
        <Button size="sm" color="secondary" outline {...getTestId(testIdBase, '.', 'template')} onClick={downloadTemplate}>
          {action.templateLabel ?? <Trans>Download Template</Trans>}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OcifHolidayHistoryButton
// ---------------------------------------------------------------------------

interface OcifHolidayHistoryButtonProps {
  action: CompiledAction;
  scope: Scope;
  fn: FnBindings;
  testIdBase?: string;
}

function OcifHolidayHistoryButton({ action, testIdBase }: OcifHolidayHistoryButtonProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [holidays, setHolidays] = useState<RowObject[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const auth = useContext(AuthContext);
  const { user: { user: { actualOrganizationalUnitId } = {} } = {} } = auth as any;
  const { i18n } = useLingui();
  const dateFormatter = useFormatter('date-only-utc');

  const open = async () => {
    setIsOpen(true);
    if (holidays !== null) return;
    setLoading(true);
    setFetchError(null);
    try {
      let orgUnitId = actualOrganizationalUnitId;
      if (!orgUnitId && (auth as any)?.user?.organizationalUnitRoles?.length > 0) {
        orgUnitId = (auth as any).user.organizationalUnitRoles[0].organizationalUnitId;
      }
      const resource = `uwe-entities/${encodeURIComponent('Holiday')}/list/${orgUnitId}`;
      const data = await UWEEntityApi.getWorkTray({ resource, offset: 0, size: 2000, sortBy: '-metadata.date' });
      setHolidays(Array.isArray(data) ? data : data.items ?? []);
    } catch (e) {
      setFetchError(e?.message ?? 'Error loading holidays');
    } finally {
      setLoading(false);
    }
  };

  const byYear = useMemo(() => {
    if (!holidays) return {} as Record<string, RowObject[]>;
    const withDate = holidays.filter(h => !!h?.metadata?.date);
    return groupBy(withDate, h => h.metadata.date.substring(0, 4));
  }, [holidays]);

  const years = Object.keys(byYear).sort((a, b) => Number(b) - Number(a));

  return (
    <>
      <Button color={action.color || 'secondary'} {...getTestId(testIdBase, '.', 'history')} onClick={open}>
        <Icon icon="ClockCountdown" className="mr-1" />
        {action.label}
      </Button>
      <Modal isOpen={isOpen} toggle={() => setIsOpen(false)} size="lg" scrollable>
        <ModalHeader toggle={() => setIsOpen(false)}>
          <Trans>Holiday History</Trans>
        </ModalHeader>
        <ModalBody>
          {loading && <Loader />}
          {fetchError && <Notification color="danger">{fetchError}</Notification>}
          {!loading && !fetchError && years.length === 0 && (
            <p className="text-muted text-center">
              <Trans>There are no recorded holidays.</Trans>
            </p>
          )}
          {!loading &&
            !fetchError &&
            years.map(year => (
              <div key={year} style={{ marginBottom: '1.5rem' }}>
                <h5 style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{year}</h5>
                <Table size="sm" bordered striped responsive>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'center' }}>
                        <Trans>Name</Trans>
                      </th>
                      <th style={{ textAlign: 'center' }}>
                        <Trans>Date</Trans>
                      </th>
                      <th style={{ textAlign: 'center' }}>
                        <Trans>Notes</Trans>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {byYear[year].map((h, idx) => (
                      <tr key={h.id ?? idx}>
                        <td style={{ textAlign: 'center' }}>{h.metadata?.name}</td>
                        <td style={{ textAlign: 'center' }}>{dateFormatter.apply(h.metadata?.date, i18n.locale)}</td>
                        <td style={{ textAlign: 'center' }}>{h.metadata?.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ))}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsOpen(false)}>
            <Trans>Close</Trans>
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

const BUTTONS_COMPONENTS: Record<string, React.ComponentType<any>> = {
  confirmbutton: ConfirmButton,
  reload: ReloadButton,
  requestreportextension: RequestExtensionButton,
  extensioninfobadge: InfoBadge,
  ocifbulkholidaybuttons: OcifBulkHolidayButtons,
  ocifholidayhistorybutton: OcifHolidayHistoryButton,
};

// ---------------------------------------------------------------------------
// ActionButtonsCell
// ---------------------------------------------------------------------------

interface ActionButtonsCellProps {
  actions: WorkTrayAction[];
  rowObject?: RowObject;
  scope: Scope;
  fnBindings?: FnBindings;
  testIdBase?: string;
  size?: 'xs';
}

export function ActionButtonsCell({ actions, rowObject, scope, fnBindings, testIdBase, size }: ActionButtonsCellProps): JSX.Element {
  const auth = useContext(AuthContext);

  const buttons = useMemo<CompiledAction[]>(
    () =>
      actions.map(obj => {
        const newObj: CompiledAction = { ...obj };

        if (obj.requireExpr) {
          newObj.requireJnx = new Jnx(obj.requireExpr);
        }

        return newObj;
      }),
    [actions]
  );

  const filteredActions = useMemo(
    () =>
      buttons.filter(obj => {
        const requireJnx = obj.requireExpr ? new Jnx(obj.requireExpr) : null;
        return (
          (!requireJnx || requireJnx.eval(rowObject, '', { ...scope, auth })) &&
          (!obj.require || obj.require.some(requirement => checkButtonAccess(requirement, rowObject, auth))) &&
          (!obj.restrict || obj.restrict.every(requirement => !checkButtonAccess(requirement, rowObject, auth)))
        );
      }),
    [auth, rowObject, scope, buttons]
  );

  return (
    <>
      {filteredActions.map((action, idx) => {
        const Component = BUTTONS_COMPONENTS[action?.type?.toLowerCase() ?? ''] || ActionButton;
        const actionTestIdBase = testIdBase ? `${testIdBase}.action-${actionSlug(action)}` : undefined;

        return (
          <Component
            size={size || 'sm'}
            fn={fnBindings}
            key={idx}
            action={action}
            rowObject={rowObject}
            scope={scope}
            testIdBase={actionTestIdBase}
          />
        );
      })}
    </>
  );
}

function checkButtonAccess({ role, permission, flag }: ActionRequirement, rowObject: RowObject | undefined, auth: unknown): boolean {
  return (role ? checkAccess(auth, role, permission) : true) && (flag ? getObject(rowObject, flag) : true);
}

// ---------------------------------------------------------------------------
// ActionButton
// ---------------------------------------------------------------------------

interface ActionButtonProps {
  action: CompiledAction;
  rowObject: RowObject;
  scope: Scope;
  fn?: FnBindings;
  testIdBase?: string;
  size?: 'sm' | 'xs';
}

function ActionButton({ action, rowObject, scope: propScope, testIdBase, size = 'sm' }: ActionButtonProps): JSX.Element | null {
  const [loading, , loadFn] = useLoader();
  const scope = useMemo<Scope>(
    () => ({
      id: rowObject?.id,
      entityTypeSlug: rowObject?.entityTypeSlug,
      item: rowObject,
      ...propScope,
      loadFn,
    }),
    [rowObject, propScope, loadFn]
  );

  const {
    onClick: actionOnClick,
    label,
    target,
    route: actionRoute,
    routeExpr: actionRouteExpr,
    color: actionColor,
    backgroundColor: actionBackgroundColor,
    icon,
  } = action;

  const route = useRouteInterpolation(actionRoute, actionRouteExpr, scope);
  const onClick = useMemo<((rowObject: RowObject, scope: Scope) => void) | null>(() => {
    if (!actionOnClick) return null;
    if (typeof actionOnClick === 'function') return actionOnClick;
    const jnx = new Jnx(actionOnClick);

    return (rowObject, scope) => jnx.eval(rowObject, '', { scope });
  }, [actionOnClick]);

  const dataCy = `btn_${label}${scope?.id ? `_${scope.id}` : ''}`;

  const { backgroundColor, color } = useMemo<{ backgroundColor?: string; color?: string }>(() => {
    const colorStr = actionColor || actionBackgroundColor || '';

    if (['main', 'primary', 'secondary', 'info', 'warning', 'danger'].includes(colorStr)) {
      return { color: colorStr };
    }

    if (colorStr.startsWith('#') || colorStr.startsWith('rgb(')) {
      return { backgroundColor: colorStr };
    }

    return {};
  }, [action]);

  const testIdProps = getTestId(testIdBase);

  return loading ? (
    <Loader dots />
  ) : route ? (
    target ? (
      <a
        style={{ backgroundColor: backgroundColor ?? undefined }}
        className={`btn btn-${size} btn-${color || 'secondary'}`}
        data-cy={dataCy}
        {...testIdProps}
        href={route}
        target={target}
        onClick={(e: MouseEvent<HTMLAnchorElement>) => {
          window.open((e.target as HTMLAnchorElement).href, target, 'noopener,noreferrer');
          e.preventDefault();
        }}
      >
        {icon && <Icon icon={icon} className="mr-1" />}
        {label}
      </a>
    ) : (
      <Link
        style={{ backgroundColor: backgroundColor ?? undefined }}
        className={`btn btn-${size} btn-${color || 'secondary'}`}
        data-cy={dataCy}
        {...testIdProps}
        to={route}
      >
        {icon && <Icon icon={icon} className="mr-1" />}
        {label}
      </Link>
    )
  ) : onClick ? (
    <Button
      size={size}
      style={{ backgroundColor: actionBackgroundColor ?? undefined }}
      color={actionColor || 'secondary'}
      data-cy={dataCy}
      {...testIdProps}
      onClick={() => onClick(rowObject, scope)}
    >
      {icon && <Icon icon={icon} className="mr-1" />}
      {label}
    </Button>
  ) : null;
}

// ---------------------------------------------------------------------------
// ConfirmButton
// ---------------------------------------------------------------------------

interface OnConfirmParams {
  confirm: ConfirmConfig & { api: string };
  fn: FnBindings;
}

async function onConfirm({ confirm, fn }: OnConfirmParams): Promise<void> {
  const WORKFLOW_SELECT = () => {
    return ['ConfirmationModal', confirm?.title || '', confirm?.text || ''] as const;
  };

  const wf = await fn.openModal(...WORKFLOW_SELECT());

  if (wf) {
    try {
      const response = await (UweApi as any)[confirm.method]({ api: confirm.api });
      if (response) fn.onSearch();
    } catch (error) {
      fn.setConfirmError(error);
    }
  }
}

interface ConfirmButtonProps {
  action: CompiledAction & { confirm: ConfirmConfig };
  rowObject: RowObject;
  scope: Scope;
  fn: FnBindings;
  testIdBase?: string;
}

function ConfirmButton({ action, rowObject, scope: propScope, fn, testIdBase }: ConfirmButtonProps): JSX.Element {
  const scope = useMemo<Scope>(
    () => ({
      id: rowObject?.id,
      entityTypeSlug: rowObject?.entityTypeSlug,
      item: rowObject,
      ...propScope,
    }),
    [rowObject, propScope]
  );

  const api = interpolate(action.confirm.api, scope);

  let dataCy = `btn_confirm_${action.label}`;
  if (scope?.id) dataCy += `_${scope.id}`;

  return (
    <Button
      size="sm"
      className="btn btn-primary"
      color={action.color || 'primary'}
      data-cy={dataCy}
      {...getTestId(testIdBase)}
      onClick={() => onConfirm({ fn, confirm: { ...action?.confirm, api } })}
    >
      {action.label}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// ReloadButton
// ---------------------------------------------------------------------------

const DEFAULT_PERIODS = [1, 5, 15, 30, 60, 0];

interface ReloadButtonProps {
  action: CompiledAction;
  scope: Scope & { lastReload?: number; reload: () => void };
  testIdBase?: string;
}

function ReloadButton({ action, scope, testIdBase }: ReloadButtonProps): JSX.Element {
  const { period: defaultPeriod, periods: _periods } = action;
  const periods = _periods || DEFAULT_PERIODS;
  const [period, setPeriod] = useState<number | undefined>(defaultPeriod);
  const paused = !period;
  const { lastReload, reload } = scope;
  const nextReload = useMemo(() => (lastReload ?? new Date().getTime()) + (period ?? 0) * 1000, [period, lastReload]);

  let dataCy = `btn_reload_${action.label}`;
  if (scope?.id) dataCy += `_${scope.id}`;

  useEffect(() => {
    if (paused) return;
    const timeLeft = nextReload - new Date().getTime();

    if (timeLeft < 0) {
      reload();
    } else {
      const timeout = setTimeout(() => {
        reload();
      }, Math.max(timeLeft, 10));

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [paused, nextReload]);

  return (
    <ButtonGroup>
      <Button size="sm" className="btn btn-secondary" color="" data-cy={dataCy} {...getTestId(testIdBase)} onClick={() => reload()}>
        {action.label || <Icon icon="ArrowClockwise" />}
      </Button>
      <UncontrolledDropdown size="sm" className="user-nav-dropdown simple-dropdown-toggle">
        <DropdownToggle size="sm" className="btn btn-secondary" color="" caret nav {...getTestId(testIdBase, '.', 'period-toggle')}>
          {period ? `${period}s` : 'Off'}
        </DropdownToggle>
        <DropdownMenu right>
          {periods.map((p, index) => (
            <DropdownItem key={index} onClick={() => setPeriod(p)} {...getTestId(testIdBase, '.', 'period', '-', p ?? 'off')}>
              {p ? `${p}s` : 'Off'}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </UncontrolledDropdown>
    </ButtonGroup>
  );
}

// ---------------------------------------------------------------------------
// Info Badge
// ---------------------------------------------------------------------------
function InfoBadge() {
  const extensionsContext = useContext(OcifExtensionsContext);
  const { extensionsThisYear, loading, setFetchExtensions = () => {} } = extensionsContext || {};

  useEffect(() => {
    setFetchExtensions(true);
  }, []);

  if (loading || extensionsThisYear == null) return null;

  const color = extensionsThisYear >= 2 ? 'danger' : extensionsThisYear ? 'warning' : 'info';
  return (
    <CustomBadge color={color} pill style={{ paddingLeft: '16px', paddingRight: '16px' }}>
      <Trans>{extensionsThisYear} of 2 extensions used this year</Trans>
    </CustomBadge>
  );
}

// ---------------------------------------------------------------------------
// Filter helpers
// ---------------------------------------------------------------------------
export const parseSearchFormDefinition = (element: WorkTrayElement): [ReturnType<typeof parseFormDefinition> | undefined, Record<string, string>] => {
  const { filters } = element;
  if (filters) {
    const sFilters: Record<string, string> = {};
    const schema = filters.reduce<Record<string, Omit<WorkTrayFilter, 'key'>>>((_, { key, ...obj }) => {
      _[key] = {
        ...obj,
      };

      return _;
    }, {});

    return [
      parseFormDefinition({
        schemaProps: {
          ':classNames': calculateColumns(filters.length),
        },
        schema,
      }),
      sFilters,
    ];
  }

  return [undefined, {}];
};

function calculateColumns(n: number): string {
  if (n === 0) return 'dont-show-component';
  // Max 3 columns. Mirrors the cols logic in SearchBar.
  // n=1 → 2-col grid, n=2 → 3-col grid, n=3 → 2-col grid, n≥4 → 3-col grid.
  const cols = n <= 2 ? n + 1 : n === 3 ? 2 : 3;
  return `grid-${cols}-columns`;
}

export default WorkTray;
